// booking-create.component.ts

import {Component, OnInit, OnDestroy, Inject, PLATFORM_ID} from '@angular/core';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {CurrencyPipe, isPlatformBrowser} from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
  ReactiveFormsModule,
  ValidatorFn
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';

import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { BookingService } from '../../services/booking/booking.service';
import { ServiceService } from '../../services/service/service.service';
import { AuthenticationService } from '../../../core/services/authentication/authentication.service';

import { Service } from '../../../core/entities/service/service.entity';
import { User, UserRoles } from '../../../core/entities/user/user.entity';
import { HeaderComponent } from '../../components/header/header.component';

import { DateTime, Duration } from 'luxon';
import {HelperBannerComponent} from '../../../core/components/helper-banner/helper-banner.component';
import {AppImageComponent} from '../../../core/components/image/image';
import {MetaPixelService} from '../../../core/services/analytics/meta-pixel.service';
import {
  SegmentedControlComponent,
  SegmentedControlOption,
} from '../../../core/components/segmented-control/segmented-control.component';
import {
  DateTimePickerComponent,
  DateTimeSelection
} from '../../components/date-time-picker/date-time-picker.component';
import {InlineCarouselComponent} from '../../../core/components/inline-carousel/inline-carousel.component';
import {Photo} from '../../../core/entities/photo/photo.entity';
import {ServicePhoto} from '../../../core/entities/service/service-photo.entity';

// --- Validators ---
function futureDateValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value) return null;

  const now = new Date();
  const selected = new Date(value);

  return selected.getTime() > now.getTime() ? null : { notInFuture: true };
}

@Component({
  selector: 'app-booking-create',
  imports: [
    TranslocoPipe,
    CurrencyPipe,
    ReactiveFormsModule,
    HeaderComponent,
    HelperBannerComponent,
    AppImageComponent,
    SegmentedControlComponent,
    DateTimePickerComponent,
    RouterLink,
    InlineCarouselComponent
  ],
  templateUrl: './booking-create.component.html',
  standalone: true,
  styleUrl: './booking-create.component.scss'
})
export class BookingCreateComponent implements OnInit, OnDestroy {
  public service: Service | null = null;
  public bookingForm!: FormGroup;
  public photographerSlug: string | null = null;
  public user: User | null = null;
  public selectedDateTime: DateTime | null = null;
  public submitting = false;
  public discount: number | null = 1;

  public paymentOptions: SegmentedControlOption[] = [];
  private static readonly PAYMENT_MODE_FULL: PaymentMode = 'full';
  private static readonly PAYMENT_MODE_DEPOSIT: PaymentMode = 'deposit';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly serviceService: ServiceService,
    private readonly fb: FormBuilder,
    private readonly toastr: ToastrService,
    private readonly transloco: TranslocoService,
    private readonly bookingService: BookingService,
    private readonly metaPixel: MetaPixelService,
    private readonly authenticationService: AuthenticationService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.user = this.authenticationService.getCurrentUser();
    this.bookingForm = this.fb.group({
      date: [null, [Validators.required, futureDateValidator]],
      hour: [null, [Validators.required]],
      minute: [null, [Validators.required]],
      price: [null, [Validators.min(0)]],
      note: [null, Validators.required],
      firstname: [this.user?.firstname ?? '', Validators.required],
      lastname: [this.user?.lastname ?? '', Validators.required],
      email: [this.user?.email ?? '', [Validators.required]],
      paymentMode: [BookingCreateComponent.PAYMENT_MODE_FULL],
    });
  }

  ngOnInit(): void {
    this.getPhotographerSlug();
    this.loadService();
  }

  ngOnDestroy(): void {
  }

  public onDateTimeSelected(selection: DateTimeSelection): void {
    this.selectedDateTime = selection.dateTime;
    this.bookingForm.patchValue({
      date: selection.date,
      hour: selection.dateTime.hour,
      minute: selection.dateTime.minute,
    });
  }

  public onDateSelected(date: string): void {
    this.selectedDateTime = null;
    this.bookingForm.patchValue({
      date,
      hour: null,
      minute: null,
    });
  }

  // --- Booking logic ---

  public onSubmit(): void {
    if (this.submitting) {
      return;
    }

    if (this.bookingForm.invalid || !this.service) {
      this.bookingForm.markAllAsTouched();
      this.showError('marketplace.booking.create.select-date-form.errors.content');
      return;
    }

    if (!this.selectedDateTime) {
      this.showError('marketplace.booking.create.select-date-form.select-a-date.time.error');
      return;
    }

    this.submitting = true;

    const { price, note, email, firstname, lastname } = this.bookingForm.value;
    const customerFirstname = firstname ?? this.user?.firstname ?? null;
    const customerLastname = lastname ?? this.user?.lastname ?? null;

    // Build the selected time in the user's local timezone
    const selectedLocal = this.selectedDateTime.setZone(DateTime.local().zoneName);
    const selectedUtc = selectedLocal.toUTC();

    const depositPreference = this.getSelectedDepositValue();

    this.bookingService.createBooking(
      this.service.id,
      email,
      customerFirstname,
      customerLastname,
      price,
      note,
      selectedUtc,
      depositPreference
    ).subscribe({
      next: (result) => {
        this.metaPixel.trackBookingIntent({
          bookingId: result.booking.id,
          service: this.service,
          datetime: selectedUtc,
          amount: price ?? result.booking.publicPrice ?? result.booking.price ?? null,
          currency: 'EUR',
          user: this.user,
          roleHint: UserRoles.customer,
          eventId: `booking-${result.booking.id}`,
          photographerId: result.booking.service?.photographerId ?? null,
        });
        if (isPlatformBrowser(this.platformId)) {
          window.location.href = result.checkoutUrl;
        }
      },
      error: () => {
        this.showError('marketplace.booking.create.select-date-form.errors.content');
        this.submitting = false;
      }
    });
  }

  // --- Private helpers ---
  private getPhotographerSlug(): void {
    this.photographerSlug = this.route.snapshot.paramMap.get('slug');
  }

  private loadService(): void {
    const idParam = this.route.snapshot.paramMap.get('service');
    if (!idParam) return;
    const id = Number(idParam);
    this.serviceService.getService(id).subscribe({
      next: (service) => {
        this.service = service;
        this.discount = this.service.discountActive ? this.service.discount : 1;
        this.bookingForm.patchValue({
          price: ((service.priceRangeMaxPublic ?? 0) * (1 - (service.discount ?? 0) / 100)),
        });
        this.applyPriceValidators(service);
        this.refreshPaymentOptions();
      },
      error: (error) => console.error('Failed to fetch service', error)
    });
  }

  public getMainImage(): string | null {
    return this.service?.image ?? this.service?.photos?.[0]?.url ?? null;
  }

  public getGalleryPhotos(): Photo[] {
    if (!this.service?.photos) return [];
    return this.service.photos.map((p: ServicePhoto, index: number) => ({
      id: p.id ?? index,
      url: p.url,
      createdAt: p.createdAt ? new Date(p.createdAt) : new Date(),
      updatedAt: p.updatedAt ? new Date(p.updatedAt) : null,
    }));
  }

  private showError(contentKey: string): void {
    this.toastr.error(
      this.transloco.translate(contentKey),
      this.transloco.translate('marketplace.booking.create.select-date-form.errors.title')
    );
  }

  public getFormattedServiceDescription(service: Service): string {
    const bio = service.description ?? '';
    return bio.replace(/\n/g, '<br>');
  }

  /** Add/remove validators on price based on service pricing mode */
  private applyPriceValidators(service: Service): void {
    const priceCtrl = this.bookingForm.get('price');
    if (!priceCtrl) return;

    // Base validators you may want to keep (e.g., decimal format)
    const baseValidators: ValidatorFn[] = [
      // Optional: accepts integers or decimals with , or .
      (ctrl: AbstractControl) => {
        if (ctrl.disabled || ctrl.value == null || ctrl.value === '') return null;
        const v = String(ctrl.value).trim();
        const ok = /^\d+([.,]\d{1,2})?$/.test(v);
        return ok ? null : { priceFormat: true };
      }
    ];

    if (service.priceRangeActive) {
      // User must enter a value in the allowed range
      const validators: ValidatorFn[] = [
        ...baseValidators,
        Validators.required,
        ...(service.priceRangeMinPublic != null ? [Validators.min(((service.priceRangeMinPublic ?? 0) * ((service.discount ?? 0) / 100)))] : []),
        ...(service.priceRangeMaxPublic != null ? [Validators.max(((service.priceRangeMaxPublic ?? 0) * ((service.discount ?? 0) / 100)))] : []),
      ];

      priceCtrl.enable({ emitEvent: false });
      priceCtrl.setValidators(validators);
    } else {
      // Fixed price: make the field read-only and not required
      priceCtrl.setValue(service.publicPrice ?? null, { emitEvent: false });
      priceCtrl.clearValidators();
      priceCtrl.disable({ emitEvent: false }); // prevents user edits
    }

    priceCtrl.updateValueAndValidity({ emitEvent: false });
  }

  private refreshPaymentOptions(): void {
    this.paymentOptions = [
      {
        label: this.transloco.translate('marketplace.booking.create.payment.full.label'),
        description: this.transloco.translate('marketplace.booking.create.payment.full.description'),
        value: BookingCreateComponent.PAYMENT_MODE_FULL,
      },
    ];

    if (this.canPayDeposit()) {
      const percentage = this.service?.depositPercentage ?? 0;
      this.paymentOptions.push({
        label: this.transloco.translate('marketplace.booking.create.payment.deposit.label', { percentage }),
        description: this.transloco.translate('marketplace.booking.create.payment.deposit.description', { percentage }),
        value: BookingCreateComponent.PAYMENT_MODE_DEPOSIT,
      });
    } else {
      this.bookingForm.patchValue({ paymentMode: BookingCreateComponent.PAYMENT_MODE_FULL });
    }
  }

  public canPayDeposit(): boolean {
    return this.service !== null
      && this.service.allowDeposit === true
      && this.service.depositPercentage !== null;
  }

  public onPaymentModeChange(value: string | number): void {
    if (typeof value !== 'string') {
      return;
    }

    if (
      value === BookingCreateComponent.PAYMENT_MODE_DEPOSIT &&
      !this.canPayDeposit()
    ) {
      return;
    }

    this.bookingForm.patchValue({ paymentMode: value as PaymentMode });
  }

  private getSelectedDepositValue(): number | null {
    const paymentMode = this.bookingForm.get('paymentMode')?.value as PaymentMode;
    if (
      paymentMode === BookingCreateComponent.PAYMENT_MODE_DEPOSIT &&
      this.canPayDeposit()
    ) {
      return this.service?.depositPercentage ?? null;
    }

    return null;
  }

  public get activeLocale(): string {
    return this.transloco.getActiveLang();
  }

  public formatDuration(minutes: number | null | undefined): string | null {
    if (minutes == null) return null;
    const locale = this.activeLocale;
    const duration = Duration.fromObject({ minutes }).shiftTo('hours', 'minutes');
    const hours = Math.floor(duration.hours);
    const remainingMinutes = Math.round(duration.minutes);
    const formatNumber = (value: number) => new Intl.NumberFormat(locale).format(value);

    if (hours > 0 && remainingMinutes > 0) {
      return `${formatNumber(hours)}h${remainingMinutes.toString().padStart(2, '0')}`;
    }
    if (hours > 0) {
      return `${formatNumber(hours)}h`;
    }
    return `${formatNumber(remainingMinutes)} min`;
  }
}

type PaymentMode = 'full' | 'deposit';
